import { Capacitor } from "@capacitor/core";
import { createInertiaApp, router } from "@inertiajs/vue3";
import { createApp, h } from "vue";

import common from "./vue/app.js";
import PageTransitionLoader from "./vue/components/PageTransitionLoader.vue";
import { i18n } from "./vue/i18n";
import { initializeMobileDeepLinks } from "./vue/mobileSsoRecovery";
import MicConsentDialog from "./vue/components/MicConsentDialog.vue";
import SessionEndedDialog from "./vue/SessionEndedDialog.vue";
import SessionExpirationDialog from "./vue/SessionExpirationDialog.vue";
import { clearHistoryOnSignedOutPages } from "./vue/utils/browserHistory";
import { installVueErrorHandler, installWindowErrorReporting } from "./vue/utils/globalErrorReporting";
import { applyNativeSharingPolicy } from "./vue/utils/nativeSharingPolicy";
import { reportSessionEnded, sessionEndedCodeFrom } from "./vue/utils/sessionEnded";
import { installSessionEndedFetch } from "./vue/utils/sessionEndedFetch";
import { backfillBrowserTimezone } from "./vue/utils/timezoneBackfill";

const isNative = Capacitor.isNativePlatform();

// Configure native status bar — transparent overlay so page backgrounds show through.
if (isNative) {
    import("@capacitor/status-bar").then(({ StatusBar, Style }) => {
        StatusBar.setOverlaysWebView({ overlay: true });
        StatusBar.setStyle({ style: Style.Light });
    });
}

initializeMobileDeepLinks();

// Hide splash screen once the WebView content is ready.
function hideSplashScreen() {
    if (!isNative) {
        return;
    }
    import("@capacitor/splash-screen").then(({ SplashScreen }) => {
        SplashScreen.hide({ fadeOutDuration: 300 });
    });
}

// Report uncaught errors and unhandled rejections that would otherwise vanish silently;
// also silences the benign Fullstory "ResizeObserver loop" error.
installWindowErrorReporting();

// Covers every fetch in the app in one place. Installed before the app mounts so vueuse and any
// direct caller resolve the wrapper rather than the native function -- see the module for why
// patching the global is the right shape here (ENG-1667).
installSessionEndedFetch();

// Inertia only swaps the page component for a response carrying the X-Inertia header; a CSRF 403
// deliberately omits it (see _csrf_failure_json in vpoc/views.py) and lands here instead.
// Cancelling the event suppresses Inertia's own error modal and leaves the current page -- and
// anything unsaved in it -- exactly as it was, with the dialog opening on top (BUG-947).
if (isNative) {
    router.on("navigate", (event) => {
        void applyNativeSharingPolicy(event.detail.page.props.organization_details);
    });
}

clearHistoryOnSignedOutPages();

router.on("invalid", (event) => {
    const response = event.detail.response;
    if (response?.status !== 403) {
        return;
    }
    // Inertia has already run the body through JSON.parse by this point (Response.getDataFromResponse),
    // so a non-object here means the 403 was something else -- a WAF block page, say.
    const body = response.data;
    const code = sessionEndedCodeFrom(body);
    if (!code) {
        return;
    }
    event.preventDefault();
    reportSessionEnded(code, body.login_url ?? null);
});

createInertiaApp({
    id: "app",
    title: (title) => (title ? `${title} - Nadia` : "Valence - Nadia"),
    resolve: (name) => {
        const pages = import.meta.glob("./vue/pages/*.vue", { eager: true });
        const path = `./vue/pages/${name}.vue`;
        return pages[path];
    },
    setup({ el, App, props, plugin }) {
        const app = createApp({ render: () => [h(App, props), h(PageTransitionLoader), h(SessionEndedDialog), h(SessionExpirationDialog), h(MicConsentDialog)] });
        installVueErrorHandler(app);
        app.use(common, props.initialPage.props);
        app.use(plugin);
        app.use(i18n);
        app.mount(el);
        hideSplashScreen();
        void applyNativeSharingPolicy(props.initialPage.props.organization_details);
        // Fire and forget: useFetch reports its own failures, and a missing timezone is
        // re-attempted on the next page load, so nothing here should delay the mount.
        backfillBrowserTimezone(props.initialPage.props.user_details).catch(() => {});
    },
});
